import { v4 as uuidv4 } from 'uuid';
import { Input } from 'src/components/Input';
import { SkillCard } from './SkillCard';
import { SelectBox } from 'src/components/SelectBox';
import Checkbox from 'src/components/Checkbox';
import { Label } from 'src/components/Label';
import Accordion from 'src/components/Accordion';
import { Button } from 'src/components/Button';
import { Card } from 'src/components/Card';
import {
  AutoCompleteSelectBox,
  Option,
} from 'src/components/AutoCompleteSelectBox';
import useAuthedRequest from 'src/hooks/useAuthedRequest';
import { useCallback, useEffect, useState } from 'react';
import { AccountConnection } from 'src/interfaces/accountConnection.interface';
import { providerDisplayNames } from 'src/utils/provider';
import { getFirstAndLastFourUUID } from 'src/utils/number';
import { providerOptions } from 'src/pages/AccountConnections/providerOptions';
import _ from 'lodash';
import { Copy, X } from '@phosphor-icons/react';

enum HTTPMethod {
  POST = 'POST',
  GET = 'GET',
  PUT = 'PUT',
  DELETE = 'DELETE',
  PATCH = 'PATCH',
}

type ExternalApiTarget = {
  trigger_condition: string;
  url: string;
  method: HTTPMethod;
  id?: string; // Identifier for frontend rendering
  notify_user?: boolean;
  headers?: Record<string, string>;
  auth_id?: string;
  input_schema?: Record<string, any>;
  purpose?: string;
  guardrail_condition?: string;
};

type ExternalApiConfig = {
  targets: ExternalApiTarget[];
};

const generateStubExternalApiTarget = (): ExternalApiTarget => {
  return {
    id: uuidv4(),
    trigger_condition: '',
    url: '',
    method: HTTPMethod.POST,
    notify_user: true,
    headers: {},
    auth_id: '',
    input_schema: { type: 'object', required: [], properties: {} },
    purpose: '',
    guardrail_condition: '',
  };
};

export const validateExternalApiConfig = (config: ExternalApiConfig) => {
  return (config.targets || []).every((target) => {
    const hasAllRequiredFields =
      target.url && target.method && target.trigger_condition;
    const validateInputSchema = (inputSchema: Record<string, any>) => {
      return Object.keys(inputSchema.properties).every((key) => {
        return (
          inputSchema.properties[key] &&
          inputSchema.properties[key].type &&
          inputSchema.properties[key].description
        );
      });
    };
    const validateHeaders = (headers: Record<string, string>) => {
      return Object.keys(headers).every((key) => {
        return headers[key] && headers[key].trim() !== '';
      });
    };
    return (
      !!hasAllRequiredFields &&
      validateInputSchema(target.input_schema || {}) &&
      validateHeaders(target.headers || {})
    );
  });
};

export function ExternalApiSkill({
  config,
  onConfigChange,
}: {
  config: ExternalApiConfig;
  onConfigChange?: (config: ExternalApiConfig) => void;
}) {
  const apiTargets = config.targets || [generateStubExternalApiTarget()];

  const handleApiTargetConfigChange = useCallback(
    (apiTarget: ExternalApiTarget, index: number) => {
      onConfigChange?.({
        ...config,
        targets: apiTargets.map((t, i) => (i === index ? apiTarget : t)),
      });
    },
    [config, apiTargets, onConfigChange],
  );

  return (
    <SkillCard description="Set up ability to call external APIs.">
      {apiTargets.map((apiTarget, index) => (
        <div
          className="mb-2 flex items-start gap-2"
          key={apiTarget.id || `${index}`}
        >
          <div className="flex-1">
            <ExternalApiSkillCard
              apiTarget={apiTarget}
              index={index}
              onConfigChange={handleApiTargetConfigChange}
            />
          </div>
          <Button
            variant="text"
            size="small"
            color="primary"
            onClick={() => {
              onConfigChange?.({
                ...config,
                targets: [...config.targets, { ...apiTarget, id: uuidv4() }],
              });
            }}
          >
            <Copy className="h-4 w-4" />
          </Button>
          <Button
            variant="text"
            size="small"
            color="error"
            className="bg-transparent hover:bg-error-100"
            onClick={() => {
              onConfigChange?.({
                ...config,
                targets: apiTargets.filter((_, i) => i !== index),
              });
            }}
            disabled={apiTargets.length <= 1}
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      ))}
      <Button
        className="mt-4 w-full"
        variant="outlined"
        color="primary"
        onClick={() =>
          onConfigChange?.({
            ...config,
            targets: [...config.targets, generateStubExternalApiTarget()],
          })
        }
      >
        + Add Another API
      </Button>
    </SkillCard>
  );
}

const ExternalApiSkillCard = ({
  apiTarget,
  index,
  onConfigChange,
}: {
  apiTarget: ExternalApiTarget;
  index: number;
  onConfigChange?: (apiTarget: ExternalApiTarget, index: number) => void;
}) => {
  const { get } = useAuthedRequest();
  const [accountConnections, setAccountConnections] = useState<Option[]>([]); // Shortcut since we don't expect many account connections
  const methods = Object.values(HTTPMethod).map((method) => ({
    value: method,
    label: method,
  }));
  const inputFieldTypes = ['string', 'number', 'boolean'];
  const inputFieldTypeOptions = inputFieldTypes.map((type) => ({
    value: type,
    label: type,
  }));

  const parseInputSchema = useCallback((inputSchema: Record<string, any>) => {
    return Object.entries(inputSchema.properties).map(([key, value]) => ({
      name: key,
      type: (value as { type: string }).type,
      description: (value as { description: string }).description,
      required: inputSchema.required?.includes(key),
    }));
  }, []);
  const [inputFields, setInputFields] = useState<
    { name: string; type: string; description: string; required: boolean }[]
  >(
    apiTarget.input_schema &&
      parseInputSchema(apiTarget.input_schema).length > 0
      ? parseInputSchema(apiTarget.input_schema)
      : [],
  );
  const [headers, setHeaders] = useState<Record<string, string>[]>(
    apiTarget.headers && Object.keys(apiTarget.headers).length > 0
      ? Object.entries(apiTarget.headers).map(([name, value]) => ({
          name,
          value,
        }))
      : [],
  );

  useEffect(() => {
    onConfigChange?.(
      {
        ...apiTarget,
        input_schema: {
          type: 'object',
          properties: inputFields.reduce(
            (acc, field) => ({
              ...acc,
              [field.name]: {
                type: field.type,
                description: field.description,
              },
            }),
            {},
          ),
          required: inputFields
            .filter((field) => field.required)
            .map((field) => field.name),
        },
      },
      index,
    );
  }, [inputFields]);

  useEffect(() => {
    onConfigChange?.(
      {
        ...apiTarget,
        headers: headers?.reduce(
          (acc, h) => ({ ...acc, [h.name]: h.value }),
          {},
        ),
      },
      index,
    );
  }, [headers]);

  const fetchAccountConnections = useCallback(async () => {
    const data = await get('/account-connections');

    const accountConnectionOptions = data.items
      .filter((ac: AccountConnection) =>
        providerOptions.find(
          (providerOption) =>
            providerOption.value === ac.type && providerOption.type === 'Auth',
        ),
      )
      .map((ac: AccountConnection) => ({
        value: ac.id,
        label: `${providerDisplayNames[ac.type]} - ${getFirstAndLastFourUUID(ac.id || '')}`,
      }));

    if (accountConnections.length === 0) {
      setAccountConnections(accountConnectionOptions);
    }

    return accountConnectionOptions;
  }, [get]);
  return (
    <Accordion
      title={`${apiTarget.method} ${apiTarget.url ? apiTarget.url : '<unknown>'}`}
      defaultOpen={index === 0}
    >
      <div className="flex gap-2 mb-4">
        <div className="flex flex-col">
          <Label>Method</Label>
          <SelectBox
            options={methods}
            value={methods.find((m) => m.value === apiTarget.method)}
            onChange={(value) =>
              onConfigChange?.(
                { ...apiTarget, method: value as HTTPMethod },
                index,
              )
            }
          />
        </div>
        <Input
          label="URL"
          placeholder="https://api.example.com/v1/endpoint"
          value={apiTarget.url}
          onChange={(e) =>
            onConfigChange?.({ ...apiTarget, url: e.target.value }, index)
          }
          required
        />
      </div>
      <div className="mb-4">
        <Input
          label="When should this API be called?"
          placeholder="e.g., When user asks to create an appointment"
          value={apiTarget.trigger_condition}
          onChange={(e) =>
            onConfigChange?.(
              { ...apiTarget, trigger_condition: e.target.value },
              index,
            )
          }
          required
        />
      </div>
      <hr />
      <Label className="text-secondary mt-2">Optional Configuration</Label>
      <Accordion
        title="Authentication"
        defaultOpen={false}
        className="border-none"
      >
        <div className="mb-4">
          <Label htmlFor="account-connection">Account Connection</Label>

          <AutoCompleteSelectBox
            defaultValue={
              apiTarget.auth_id
                ? accountConnections.find(
                    (ac) => ac.value === apiTarget.auth_id,
                  )
                : undefined
            }
            fetchOptions={fetchAccountConnections}
            onChange={(value) =>
              onConfigChange?.(
                {
                  ...apiTarget,
                  auth_id: value,
                },
                index,
              )
            }
            variant="outlined"
            color="primary"
            size="medium"
          />
        </div>
      </Accordion>
      <Accordion
        title="Request Details"
        defaultOpen={false}
        className="border-none"
      >
        <div className="mb-4">
          <Label>Payload Fields</Label>
          {inputFields.map((field, index) => (
            <div key={index} className="mb-2 flex items-center gap-2">
              <Card className="flex-1">
                <div className="flex gap-2 mb-2">
                  <Input
                    label="Field name"
                    placeholder="e.g., name"
                    value={field.name}
                    onChange={(e) =>
                      setInputFields([
                        ...inputFields.slice(0, index),
                        { ...field, name: e.target.value },
                        ...inputFields.slice(index + 1),
                      ])
                    }
                    required
                  />
                  <div className="flex flex-col">
                    <Label>Field Type</Label>
                    <SelectBox
                      options={inputFieldTypeOptions}
                      value={inputFieldTypeOptions.find(
                        (m) => m.value === field.type,
                      )}
                      onChange={(value) =>
                        setInputFields([
                          ...inputFields.slice(0, index),
                          { ...field, type: value as string },
                          ...inputFields.slice(index + 1),
                        ])
                      }
                    />
                  </div>
                </div>
                <Input
                  label="Field Purpose"
                  placeholder="e.g., Full name of the user requesting the appointment"
                  value={field.description}
                  onChange={(e) =>
                    setInputFields([
                      ...inputFields.slice(0, index),
                      { ...field, description: e.target.value },
                      ...inputFields.slice(index + 1),
                    ])
                  }
                />
                <div className="flex items-center gap-2 mt-2">
                  <Checkbox
                    ariaLabel="Required field"
                    checked={field.required}
                    onChange={(checked) =>
                      setInputFields([
                        ...inputFields.slice(0, index),
                        { ...field, required: checked },
                        ...inputFields.slice(index + 1),
                      ])
                    }
                  />
                  <span className="text-sm text-neutral-600">
                    Required field
                  </span>
                </div>
              </Card>
              <Button
                variant="text"
                size="small"
                color="error"
                className="bg-transparent hover:bg-error-100 h-12"
                onClick={() => {
                  setInputFields(inputFields.filter((_, i) => i !== index));
                }}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          ))}
          <Button
            className="mt-4 w-full"
            variant="outlined"
            color="secondary"
            onClick={() =>
              setInputFields([
                ...inputFields,
                { name: '', type: 'string', description: '', required: false },
              ])
            }
          >
            + Add Field
          </Button>
        </div>
        <div className="mb-4">
          <Label>Custom Headers</Label>
          {headers.map((header, index) => (
            <div className="flex gap-2 mb-2" key={index}>
              <Input
                placeholder="Header name"
                value={header.name}
                onChange={(e) =>
                  setHeaders([
                    ...headers.slice(0, index),
                    { name: e.target.value, value: header.value },
                    ...headers.slice(index + 1),
                  ])
                }
              />
              <Input
                placeholder="Header value"
                value={header.value}
                onChange={(e) =>
                  setHeaders([
                    ...headers.slice(0, index),
                    { name: header.name, value: e.target.value },
                    ...headers.slice(index + 1),
                  ])
                }
              />
              <Button
                variant="text"
                size="small"
                color="error"
                className="bg-transparent hover:bg-error-100"
                onClick={() => {
                  setHeaders(headers.filter((_, i) => i !== index));
                }}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          ))}
          <Button
            className="mt-4 w-full"
            variant="outlined"
            color="secondary"
            onClick={() => setHeaders([...headers, { name: '', value: '' }])}
          >
            + Add Header
          </Button>
        </div>
      </Accordion>
      <Accordion
        title="Additional Context"
        defaultOpen={false}
        className="border-none"
      >
        <div className="mb-4">
          <Input
            label="What is this API used for?"
            placeholder="e.g., Creating appointments"
            value={apiTarget.purpose}
            onChange={(e) =>
              onConfigChange?.(
                {
                  ...apiTarget,
                  purpose: e.target.value,
                },
                index,
              )
            }
          />
        </div>
        <div className="mb-4">
          <Input
            label="When should this API not be used?"
            placeholder="e.g., If user is not yet qualified for the service"
            value={apiTarget.guardrail_condition}
            onChange={(e) =>
              onConfigChange?.(
                {
                  ...apiTarget,
                  guardrail_condition: e.target.value,
                },
                index,
              )
            }
          />
        </div>
        <div className="mb-4 flex items-center gap-2">
          <Checkbox
            ariaLabel="Notify User"
            checked={apiTarget.notify_user ?? true}
            onChange={(checked) =>
              onConfigChange?.(
                {
                  ...apiTarget,
                  notify_user: checked,
                },
                index,
              )
            }
          />
          <span className="text-sm text-neutral-600">
            Notify user before calling API
          </span>
        </div>
      </Accordion>
    </Accordion>
  );
};
